import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Button,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Skeleton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import BarcodeService, { BarcodeInfo, BarcodeGenerationOptions } from '../../services/BarcodeService';

export interface BarcodeDisplayProps {
  // Core data
  accountId: string;
  objectId: string; // SKU ID
  skuData?: {
    name: string;
    itemCode?: string;
    hsn?: string;
    mrp?: number;
    salePrice?: number;
    barCodeImage?: string;
  };
  
  // Display options
  variant?: 'detailed' | 'card' | 'compact' | 'inline';
  showActions?: boolean;
  showItemInfo?: boolean;
  autoGenerate?: boolean;
  hideIfNoBarcode?: boolean; // New prop to hide component when no barcode
  hideErrorMessage?: boolean; // New prop to hide error messages
  
  // Styling options
  width?: number | string;
  height?: number | string;
  
  // Event handlers
  onBarcodeGenerated?: (barcodeData: BarcodeInfo) => void;
  onError?: (error: string) => void;
  onImageLoad?: () => void;
}

const BarcodeDisplay: React.FC<BarcodeDisplayProps> = ({
  accountId,
  objectId,
  skuData,
  variant = 'detailed',
  showActions = true,
  showItemInfo = true,
  autoGenerate = false,
  hideIfNoBarcode = false,
  hideErrorMessage = false,
  width = 200,
  height = 60,
  onBarcodeGenerated,
  onError,
  onImageLoad
}) => {
  const [loading, setLoading] = useState(false);
  const [barcodeData, setBarcodeData] = useState<BarcodeInfo | null>(null);
  const [imageError, setImageError] = useState(false);
  const [actionLoading, setActionLoading] = useState<'generate' | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Initialize barcode data from props
  useEffect(() => {
    if (skuData) {
      const data: BarcodeInfo = {
        accountId,
        objectId,
        sku: skuData.itemCode || objectId,
        itemName: skuData.name,
        itemCode: skuData.itemCode,
        hsn: skuData.hsn,
        mrp: skuData.mrp,
        salePrice: skuData.salePrice,
        barCodeImage: skuData.barCodeImage
      };
      setBarcodeData(data);
      
      // Auto-generate if no barcode exists and autoGenerate is true
      if (autoGenerate && !skuData.barCodeImage) {
        handleGenerateBarcode();
      }
    }
  }, [accountId, objectId, skuData, autoGenerate]);

  const handleGenerateBarcode = useCallback(async () => {
    setLoading(true);
    setActionLoading('generate');
    setImageError(false);
    
    try {
      const generatedBarcode = await BarcodeService.generateBarcode(objectId);
      setBarcodeData(prev => ({
        ...prev,
        ...generatedBarcode
      }));
      setImageLoaded(false); // Reset image loaded state
      setImageError(false); // Reset error state
      onBarcodeGenerated?.(generatedBarcode);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to generate barcode';
      setImageError(true);
      !hideErrorMessage && onError?.(errorMessage);
    } finally {
      setLoading(false);
      setActionLoading(null);
    }
  }, [objectId, onBarcodeGenerated, onError, hideErrorMessage]);

  const getBarcodeImageSrc = useCallback((): string => {
    if (barcodeData?.barCodeImage) {
      // Handle base64 images
      if (barcodeData.barCodeImage.startsWith('data:')) {
        return barcodeData.barCodeImage;
      }
      // Handle full URLs
      if (barcodeData.barCodeImage.startsWith('http')) {
        return barcodeData.barCodeImage;
      }
      // Use the service method for relative paths with cache busting
      const url = BarcodeService.getBarcodeImageUrl(accountId, objectId);
      return url + `?t=${Date.now()}`;
    }
    return '';
  }, [accountId, objectId, barcodeData]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
    onImageLoad?.();
  }, [onImageLoad]);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
  }, []);

  const renderBarcodeImage = () => {
    const imageSrc = getBarcodeImageSrc();
    
    if (loading && actionLoading === 'generate') {
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={height}
          sx={{ border: '1px dashed #ccc', borderRadius: 1, bgcolor: 'grey.50' }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" ml={1}>Generating...</Typography>
        </Box>
      );
    }

    if (imageError || !imageSrc) {
      // If hideIfNoBarcode is true, return null (don't render anything)
      if (hideIfNoBarcode) {
        return null;
      }
      
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          height={height}
          sx={{ border: '1px dashed #ccc', borderRadius: 1, bgcolor: 'grey.50' }}
        >
          <ErrorIcon color="disabled" />
          {!hideErrorMessage && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              No barcode available
            </Typography>
          )}
          {showActions && (
            <Button
              size="small"
              startIcon={<QrCodeIcon />}
              onClick={handleGenerateBarcode}
              disabled={actionLoading === 'generate'}
              sx={{ mt: 1 }}
            >
              Generate Barcode
            </Button>
          )}
        </Box>
      );
    }

    return (
      <Box textAlign="center" position="relative">
        {!imageLoaded && (
          <Skeleton 
            variant="rectangular" 
            width={width} 
            height={height}
            sx={{ borderRadius: 1 }}
          />
        )}
        <img
          src={imageSrc}
          alt={`Barcode for ${barcodeData?.itemCode || objectId}`}
          style={{
            width: typeof width === 'number' ? `${width}px` : width,
            height: typeof height === 'number' ? `${height}px` : height,
            objectFit: 'contain',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#fff',
            display: imageLoaded ? 'block' : 'none'
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </Box>
    );
  };

  const renderActions = () => {
    if (!showActions) return null;

    const actions = [
      {
        key: 'regenerate',
        icon: <RefreshIcon />,
        label: 'Regenerate',
        onClick: handleGenerateBarcode,
        loading: actionLoading === 'generate'
      }
    ];

    if (variant === 'compact' || variant === 'inline') {
      return (
        <Stack direction="row" spacing={0.5}>
          {actions.map((action) => (
            <Tooltip key={action.key} title={action.label}>
              <IconButton
                size="small"
                onClick={action.onClick}
                disabled={action.loading || loading}
              >
                {action.loading ? <CircularProgress size={16} /> : action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={1} mt={1}>
        {actions.map((action) => (
          <Button
            key={action.key}
            size="small"
            startIcon={action.loading ? <CircularProgress size={16} /> : action.icon}
            onClick={action.onClick}
            disabled={action.loading || loading}
            variant="outlined"
          >
            {action.label}
          </Button>
        ))}
      </Stack>
    );
  };

  const renderItemInfo = () => {
    if (!showItemInfo || !barcodeData) return null;

    const info = [
      { label: 'Item Code', value: barcodeData.itemCode },
      { label: 'HSN', value: barcodeData.hsn },
      { label: 'MRP', value: barcodeData.mrp ? `₹${barcodeData.mrp}` : undefined },
      { label: 'Sale Price', value: barcodeData.salePrice ? `₹${barcodeData.salePrice}` : undefined }
    ].filter(item => item.value);

    if (variant === 'compact') {
      return (
        <Stack direction="row" spacing={1} mt={1}>
          {info.slice(0, 2).map((item) => (
            <Chip 
              key={item.label}
              label={`${item.label}: ${item.value}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      );
    }

    return (
      <Box mt={1}>
        <Typography variant="subtitle2" gutterBottom>
          {barcodeData.itemName}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {info.map((item) => (
            <Chip 
              key={item.label}
              label={`${item.label}: ${item.value}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      </Box>
    );
  };

  // Early return if component should be hidden when no barcode exists
  if (hideIfNoBarcode && (!barcodeData?.barCodeImage || imageError)) {
    return null;
  }

  // Variant-specific rendering
  if (variant === 'inline') {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        {renderBarcodeImage()}
        {renderActions()}
      </Stack>
    );
  }

  if (variant === 'compact') {
    return (
      <Box>
        {renderBarcodeImage()}
        {renderItemInfo()}
        {renderActions()}
      </Box>
    );
  }

  // Card and detailed variants
  const content = (
    <>
      {renderBarcodeImage()}
      {renderItemInfo()}
      {renderActions()}
    </>
  );

  if (variant === 'card') {
    return (
      <Card variant="outlined" sx={{ p: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          {content}
        </CardContent>
      </Card>
    );
  }

  // Detailed variant
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Product Barcode
      </Typography>
      {content}
    </Box>
  );
};

export default BarcodeDisplay;